import { useMemo, useRef } from 'react';

import { BOOK_DEMO_ORIGIN } from 'src/data/common';
import { DEFAULT_LANGUAGE } from 'src/data/generation';
import { PRIVACY_SETTINGS_FEATURES } from 'src/data/privacy';
import { useIsProjectReadOnly } from 'src/store';
import { useUpgradePopover } from 'src/views/UpgradePopover';

import { useCustomerPrivacySettings } from '../useCustomerPrivacySettings';

import { useBrandTermsProps } from './useBrandTermsProps';

export const usePrepareBlogInsightsProps = ({
  text,
  onApplyDetectedBrandTerm,
  onDetectedBrandTermHover,
  onDetectedBrandTermClick,
  updating,
  detectedBrandTermsData,
  forceDisabled,
  markedKeywordsMap,
  targetKeywords,
  supportingKeywords,
  onToggleMarkedKeyword,
  onSeoTrackerExpand,
  plagiarismChecksUsed,
  plagiarismChecksLimit,
  hasPerformedPlagiarismCheck,
  potentialCopiedSentences,
  lastPlagiarismCheckTime,
  loadingPlagiarismReport,
  onCheckPlagiarism,
  isInsightsPanelOpen,
  onOpenPlagiarism,
  trackingEventAttributes,
  language,
}) => {
  // Keep reference to the latest tracking event attributes, so we can use them in the event handlers
  const trackingEventAttributesRef = useRef(trackingEventAttributes);
  trackingEventAttributesRef.current = trackingEventAttributes;

  const isProjectReadOnly = useIsProjectReadOnly();

  const disabled = forceDisabled || isProjectReadOnly || updating;

  const { isFeatureRestricted } = useCustomerPrivacySettings();
  const openUpgradePopover = useUpgradePopover();

  const brandTermsProps = useBrandTermsProps({
    detectedBrandTermsData,
    onApplyDetectedBrandTerm,
    onDetectedBrandTermHover,
    onDetectedBrandTermClick,
    disabled,
    loading: detectedBrandTermsData?.loading,
    language: language || DEFAULT_LANGUAGE,
  });

  const readabilityScoreProps = useMemo(
    () => ({
      copyText: text,
    }),
    [text]
  );

  const seoTrackerProps = useMemo(
    () => ({
      targetKeywords,
      supportingKeywords,
      onToggleMarkedKeyword,
      markedKeywordsMap,
      onExpand: onSeoTrackerExpand,
    }),
    [
      targetKeywords,
      supportingKeywords,
      onToggleMarkedKeyword,
      markedKeywordsMap,
      onSeoTrackerExpand,
    ]
  );

  const plagiarismCheckerProps = useMemo(() => {
    const handleCheckPlagiarism = ({ referenceElement }) => {
      if (isFeatureRestricted(PRIVACY_SETTINGS_FEATURES.EXTERNAL_PLAGIARISM_DETECTION)) {
        return;
      }

      const reachedFeatureLimit =
        plagiarismChecksLimit != null ? plagiarismChecksUsed >= plagiarismChecksLimit : false;
      if (reachedFeatureLimit) {
        openUpgradePopover({
          referenceElement,
          origin: BOOK_DEMO_ORIGIN.checkPlagiarism,
          placement: 'top-end',
        });
      } else {
        onCheckPlagiarism(text);
      }
    };

    return {
      copyText: text,
      plagiarismChecksUsed,
      plagiarismChecksLimit,
      loading: loadingPlagiarismReport,
      lastPlagiarismCheckTime,
      hasPerformedPlagiarismCheck,
      onCheckPlagiarism: handleCheckPlagiarism,
      potentialCopiedSentences,
      isInsightsPanelOpen,
      onOpenPlagiarism,
    };
  }, [
    plagiarismChecksUsed,
    plagiarismChecksLimit,
    lastPlagiarismCheckTime,
    hasPerformedPlagiarismCheck,
    onCheckPlagiarism,
    potentialCopiedSentences,
    loadingPlagiarismReport,
    text,
    isInsightsPanelOpen,
    onOpenPlagiarism,
    isFeatureRestricted,
    openUpgradePopover,
  ]);

  return useMemo(
    () => ({
      seoTrackerProps,
      plagiarismCheckerProps,
      brandTermsProps,
      readabilityScoreProps,
    }),
    [seoTrackerProps, plagiarismCheckerProps, brandTermsProps, readabilityScoreProps]
  );
};
